import type {CSSProperties} from 'react';
import {weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeSearchBarLook = 'searchbar-voice' | 'searchbar-voice-on' | 'searchbar-camera' | 'searchbar-dupe-open' | 'searchbar-dupe-pull';

const LOOKS: readonly string[] = ['searchbar-voice', 'searchbar-voice-on', 'searchbar-camera', 'searchbar-dupe-open', 'searchbar-dupe-pull'];
export const isWedgeSearchBarLook = (look: string): look is WedgeSearchBarLook => LOOKS.includes(look);

/* Released controls of the typed search bar (the voice and camera buttons in the bar, and the two buttons of the duplicate search warning),
   off-scale. The voice and camera buttons are 40px round-cornered inline flex boxes with no border; the voice button is clear with the accent
   ink and fills with the accent while it listens (its ink then reads the --wedge-search-bar-ink role, which the caller sets to the light ink),
   and the camera button fills with the accent tint. The voice button settles over 160ms and pulses while it listens; the caller turns the pulse
   off for reduced motion. The warning buttons are 40px tall flex children with 13.5px type in the Wedge font: the first is the accent fill
   with the same light ink role, the second is the surface fill with a card border. Every look leaves the padding to the user agent, as the bare buttons do, and
   keeps the source declaration order, so the shipped ink rules, which match `color: var(--accent);` and `color: var(--label);` in the serialized style,
   still reach them. None draws a transparent target (wedgeSearchBarDrawsHit): each box is 40px with a rounded corner, so a 44px square would
   be larger than the box and would add a positioned box that legacy does not have. The shared `searchbar-` prefix rule already drops the
   pseudo element. */
export const wedgeSearchBarDrawsHit = (look: string): boolean => !isWedgeSearchBarLook(look);

const INK = 'var(--wedge-search-bar-ink, inherit)';
const ICON_BOX: CSSProperties = {
  flexShrink: 0, width: 40, height: 40, borderRadius: 10, border: 0, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

export function wedgeSearchBarStyle(look: WedgeSearchBarLook): CSSProperties {
  const ease = wedgeExactRole('ease');
  switch (look) {
    case 'searchbar-voice': return {
      ...ICON_BOX, background: 'transparent', color: wedgeExactRole('accent'), animation: 'none',
      transition: `background 160ms ${ease}, color 160ms ${ease}`,
    };
    case 'searchbar-voice-on': return {
      ...ICON_BOX, background: wedgeExactRole('accent'), color: INK, animation: `prepPulse 1.6s ${ease} infinite`,
      transition: `background 160ms ${ease}, color 160ms ${ease}`,
    };
    case 'searchbar-camera': return {
      ...ICON_BOX, background: wedgeExactRole('accentTint'), color: wedgeExactRole('accent'),
    };
    case 'searchbar-dupe-open': return {
      flex: 1, height: 40, borderRadius: 10, border: 0, background: wedgeExactRole('accent'), color: INK,
      fontSize: 13.5, fontWeight: weight.bold, cursor: 'pointer', fontFamily: wedgeExactRole('font'),
    };
    case 'searchbar-dupe-pull': return {
      flex: 1, height: 40, borderRadius: 10, border: `1px solid ${wedgeExactRole('cardBorder')}`,
      background: wedgeExactRole('surface'), color: wedgeExactRole('label'),
      fontSize: 13.5, fontWeight: weight.semibold, cursor: 'pointer', fontFamily: wedgeExactRole('font'),
    };
  }
}
